"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";

import type { OverlayMenuBaseProps } from "@/app/components/menu/overlayMenuSystem";
import ShowMoreToggle from "@/app/new/ec/product/[basePath]/sections/questions/showMoreToggle";
import Button from "@/components/design-system/button";
import RadioButton from "@/components/design-system/radioButton";
import TextInputField from "@/components/design-system/textInputField";
import { setOpenMenu } from "@/store/drawerMenuOpenSlice";
import { useAppDispatch } from "@/store/hooks";
import { actions } from "@/store/searchSlice";
import {
  useGetAllShippingAddressesQuery,
  useGetUserQuery,
  useLazyGetPostalCodeDetailsQuery,
  useSetShippingAddressAsPrimaryMutation,
} from "@/store/userApi";
import { getCityAndZipcodeCookies, setCityAndZipcodeCookies } from "@/utils/locationDetails";
import styles from "./deliveryAddressMenu.module.scss";

export const DELIVERY_ADDRESS_TITLE = "Choose Delivery Address";

/** Saved addresses beyond this sit behind Show More. */
const VISIBLE_ADDRESSES = 4;

const ZIP_CODE_PATTERN = /^\d{5}$/;
const ZIP_CODE_FORMAT_ERROR = "Enter a 5-digit ZIP code.";
const ZIP_CODE_NOT_FOUND_ERROR = "ZIP code not found. Please try another ZIP code.";

/**
 * The "Choose Delivery Address" drawer: a saved address or another ZIP code becomes the delivery location in the
 * location cookies and the store. Opened with `setOpenMenu("deliveryAddress")`; callers read the cookies back
 * once it closes. Picking a saved address also makes it the account's primary address.
 */
export default function DeliveryAddressMenu({ closeCallback }: OverlayMenuBaseProps) {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const { data: user } = useGetUserQuery();
  const isSignedIn = user?.IsGuestUser === false && user.isAuthorized === true;
  const { data: addresses } = useGetAllShippingAddressesQuery(undefined, { skip: !isSignedIn });
  const [lookUpZipCode] = useLazyGetPostalCodeDetailsQuery();
  const [setShippingAddressAsPrimary] = useSetShippingAddressAsPrimaryMutation();

  const [currentZipCode] = useState(() => getCityAndZipcodeCookies().zipCode ?? "");
  const savedAddresses = useMemo(() => orderAddresses(addresses ?? [], currentZipCode), [addresses, currentZipCode]);
  const defaultSelection = getDefaultSelection(savedAddresses, currentZipCode);
  const [picked, setPicked] = useState<Selection | null>(null);
  const selection = picked ?? defaultSelection;
  const [typedZipCode, setTypedZipCode] = useState<string | null>(null);
  const zipCode = typedZipCode ?? (defaultSelection === "zipCode" ? currentZipCode : "");
  const [zipCodeError, setZipCodeError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const latestChoice = useRef({ selection, zipCode });
  useLayoutEffect(() => {
    latestChoice.current = { selection, zipCode };
  });

  const radioName = useId();
  const baseId = useId();
  const savedHeadingId = `${baseId}-saved`;
  const savedListId = `${baseId}-saved-list`;
  const zipHeadingId = `${baseId}-zip`;
  const zipInputId = `${baseId}-zip-input`;

  // A link that leaves the page, such as Manage Addresses, closes the drawer with it.
  const openedOn = useRef(pathname);
  useEffect(() => {
    if (pathname !== openedOn.current) {
      closeCallback?.();
    }
  }, [closeCallback, pathname]);

  const close = () => dispatch(setOpenMenu("none"));

  const applyLocation = (city: string, postalCode: string) => {
    setCityAndZipcodeCookies(city, postalCode);
    dispatch(actions.setShippingZipCode(postalCode.slice(0, 5)));
  };

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSaving) {
      return;
    }
    if (selection === "zipCode") {
      const entered = zipCode.trim();
      if (!ZIP_CODE_PATTERN.test(entered)) {
        setZipCodeError(ZIP_CODE_FORMAT_ERROR);
        return;
      }
      setIsSaving(true);
      const details = await lookUpZipCode(entered, true)
        .unwrap()
        .catch(() => null);
      setIsSaving(false);
      const latest = latestChoice.current;
      if (latest.selection !== "zipCode" || latest.zipCode.trim() !== entered) {
        return;
      }
      if (!details?.isValid) {
        setZipCodeError(ZIP_CODE_NOT_FOUND_ERROR);
        return;
      }
      applyLocation(details.city, details.postalCode);
      close();
      return;
    }

    const address = savedAddresses.find(({ Id }) => Id === selection.addressId);
    if (address === undefined) {
      return;
    }
    applyLocation(address.City, address.PostalCD);
    if (!address.IsPrimary) {
      setIsSaving(true);
      await setShippingAddressAsPrimary(address.Id);
      setIsSaving(false);
    }
    close();
  }

  const visibleAddresses = expanded ? savedAddresses : savedAddresses.slice(0, VISIBLE_ADDRESSES);
  const loginHref = `/login?origin=${encodeURIComponent(pathname ?? "")}`;

  return (
    <form className={styles.DeliveryAddressMenu} onSubmit={onSubmit} noValidate data-delivery-address-drawer="">
      <div className={styles.body}>
        <p className={styles.intro}>
          Select your delivery location for more accurate shipping times and price estimates.
        </p>

        {user !== undefined && (
          <section className={styles.section} aria-labelledby={savedHeadingId}>
            <div className={styles.sectionHeader}>
              <h2 id={savedHeadingId} className={styles.sectionTitle}>
                From Saved Addresses
              </h2>
              {isSignedIn && (
                <Link href="/account/shipping" prefetch={false} className={styles.link} data-delivery-address-manage="">
                  Manage Addresses
                </Link>
              )}
            </div>
            {!isSignedIn ? (
              <Link href={loginHref} prefetch={false} className={styles.link} data-delivery-address-sign-in="">
                Sign in to see your saved addresses
              </Link>
            ) : addresses !== undefined && savedAddresses.length === 0 ? (
              <p className={styles.empty}>You don’t have any saved addresses yet.</p>
            ) : (
              <ul id={savedListId} className={styles.addressList}>
                {visibleAddresses.map((address) => {
                  const radioId = `${baseId}-address-${address.Id}`;
                  return (
                    <li key={address.Id} className={styles.address}>
                      <span className={styles.radio}>
                        <RadioButton
                          id={radioId}
                          name={radioName}
                          value={String(address.Id)}
                          checked={selection !== "zipCode" && selection.addressId === address.Id}
                          onChange={() => setPicked({ addressId: address.Id })}
                          ariaLabelledBy={`${radioId}-text`}
                          data-delivery-address-option="saved"
                        />
                      </span>
                      <label id={`${radioId}-text`} htmlFor={radioId} className={styles.addressText}>
                        <span>{address.Streets.filter(Boolean).join(", ")}</span>
                        <span>
                          {address.City}, {address.RegionCD} {address.PostalCD}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            {savedAddresses.length > VISIBLE_ADDRESSES && (
              <ShowMoreToggle
                expanded={expanded}
                moreLabel="Show More"
                lessLabel="Show Less"
                controls={savedListId}
                onToggle={() => setExpanded((open) => !open)}
                data-delivery-address-show-more=""
              />
            )}
          </section>
        )}

        <section className={styles.section} aria-labelledby={zipHeadingId}>
          <h2 id={zipHeadingId} className={styles.sectionTitle}>
            Other ZIP Code
          </h2>
          <div className={styles.zipCode} data-delivery-address-zip="">
            <span className={styles.zipCodeRadio}>
              <RadioButton
                name={radioName}
                value="zipCode"
                checked={selection === "zipCode"}
                onChange={() => setPicked("zipCode")}
                ariaLabelledBy={zipHeadingId}
                data-delivery-address-option="zip"
              />
            </span>
            <TextInputField
              id={zipInputId}
              className={styles.zipCodeInput}
              label="ZIP code"
              showLabel={false}
              aria-label="ZIP code"
              placeholder="Enter ZIP Code"
              value={zipCode}
              onChange={(event) => {
                setTypedZipCode(event.target.value);
                setZipCodeError(null);
              }}
              onFocus={() => setPicked("zipCode")}
              error={zipCodeError !== null}
              errorMessage={zipCodeError ?? undefined}
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={5}
              showLeftIcon={false}
              showRightIcon={false}
            />
          </div>
        </section>
      </div>

      <div className={styles.footer}>
        <Button
          variant="secondary"
          color="blue"
          text="Cancel"
          type="button"
          showLeftIcon={false}
          showRightIcon={false}
          className={styles.footerButton}
          onClick={close}
          data-delivery-address-cancel=""
        />
        <Button
          variant="primary"
          color="blue"
          text="Update"
          type="submit"
          showLeftIcon={false}
          showRightIcon={false}
          loading={isSaving}
          className={styles.footerButton}
          data-delivery-address-update=""
        />
      </div>
    </form>
  );
}

type Selection = { addressId: number } | "zipCode";

function matchesZipCode(address: Account.Addresses.Address, zipCode: string): boolean {
  return zipCode !== "" && address.PostalCD.slice(0, 5) === zipCode;
}

/** The address the shopper is delivering to leads, else the primary one; the rest keep their order. */
function orderAddresses(addresses: Account.Addresses.Address[], zipCode: string): Account.Addresses.Address[] {
  const current =
    addresses.find((address) => address.IsPrimary && matchesZipCode(address, zipCode)) ??
    addresses.find((address) => matchesZipCode(address, zipCode)) ??
    addresses.find((address) => address.IsPrimary);
  return current === undefined ? addresses : [current, ...addresses.filter((address) => address !== current)];
}

/** The saved address for the current ZIP code; else, without one, the first saved address; else the ZIP code. */
function getDefaultSelection(addresses: Account.Addresses.Address[], zipCode: string): Selection {
  const [first] = addresses;
  if (first !== undefined && (zipCode === "" || matchesZipCode(first, zipCode))) {
    return { addressId: first.Id };
  }
  return "zipCode";
}
